import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, CardActions,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Switch, FormControlLabel, Chip, IconButton, Divider,
  Tooltip, Alert, CircularProgress, Paper, Grid, Stack,
  Table, TableBody, TableCell, TableHead, TableRow,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
  Add, Delete, Edit, PlayArrow, Stop,
  FlashOn, CallSplit, ArrowForward, CheckCircle, Error as ErrorIcon,
  ExpandMore, History, Refresh, BoltOutlined
} from '@mui/icons-material';
import { automationService } from '../services/api';

// ─── Config des triggers / actions ───────────────────────────────────────────

const TRIGGERS = [
  { value: 'task_created',        label: 'Quand une tâche est créée' },
  { value: 'task_status_changed', label: 'Quand le statut change' },
  { value: 'task_priority_changed', label: 'Quand la priorité change' },
  { value: 'task_assigned',       label: 'Quand une tâche est assignée' },
  { value: 'task_overdue',        label: 'Quand une tâche est en retard' },
];

const ACTIONS = [
  { value: 'change_status',   label: 'Changer le statut' },
  { value: 'change_priority', label: 'Changer la priorité' },
  { value: 'assign_to',       label: 'Assigner à un membre' },
  { value: 'notify_user',     label: 'Notifier un utilisateur' },
  { value: 'notify_manager',  label: 'Notifier le manager du projet' },
];

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Non commencé' },
  { value: 'in_progress', label: 'En cours' },
  { value: 'review',      label: 'En révision' },
  { value: 'completed',   label: 'Terminé' },
  { value: 'cancelled',   label: 'Annulé' },
];

const PRIORITY_OPTIONS = [
  { value: 'low',    label: 'Basse' },
  { value: 'medium', label: 'Moyenne' },
  { value: 'high',   label: 'Haute' },
  { value: 'urgent', label: 'Urgente' },
];

const TRIGGER_COLOR = '#6366f1';
const ACTION_COLOR  = '#10b981';
const COND_COLOR    = '#f59e0b';

// ─── Helper: libellé lisible d'une règle ────────────────────────────────────

function ruleLabel(auto) {
  const trig = TRIGGERS.find(t => t.value === auto.trigger_type);
  const act  = ACTIONS.find(a  => a.value === auto.action_type);
  return `${trig?.label || auto.trigger_type} → ${act?.label || auto.action_type}`;
}

// ─── TriggerConfig : champs dynamiques selon le type de déclencheur ──────────

function TriggerConfig({ triggerType, config, onChange }) {
  if (!triggerType) return null;

  return (
    <Box sx={{ mt: 1 }}>
      {(triggerType === 'task_status_changed') && (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>De (statut)</InputLabel>
              <Select
                value={config.from_status || ''}
                label="De (statut)"
                onChange={e => onChange({ ...config, from_status: e.target.value })}
              >
                <MenuItem value=""><em>N'importe lequel</em></MenuItem>
                {STATUS_OPTIONS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Vers (statut)</InputLabel>
              <Select
                value={config.to_status || ''}
                label="Vers (statut)"
                onChange={e => onChange({ ...config, to_status: e.target.value })}
              >
                <MenuItem value=""><em>N'importe lequel</em></MenuItem>
                {STATUS_OPTIONS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      )}

      {(triggerType === 'task_priority_changed') && (
        <FormControl fullWidth size="small">
          <InputLabel>Vers (priorité)</InputLabel>
          <Select
            value={config.priority || ''}
            label="Vers (priorité)"
            onChange={e => onChange({ ...config, priority: e.target.value })}
          >
            <MenuItem value=""><em>N'importe laquelle</em></MenuItem>
            {PRIORITY_OPTIONS.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </Select>
        </FormControl>
      )}
    </Box>
  );
}

// ─── ActionConfig : champs dynamiques selon le type d'action ─────────────────

function ActionConfig({ actionType, config, onChange, members }) {
  if (!actionType) return null;

  return (
    <Box sx={{ mt: 1 }}>
      {actionType === 'change_status' && (
        <FormControl fullWidth size="small">
          <InputLabel>Nouveau statut *</InputLabel>
          <Select
            value={config.status || ''}
            label="Nouveau statut *"
            onChange={e => onChange({ ...config, status: e.target.value })}
          >
            {STATUS_OPTIONS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </Select>
        </FormControl>
      )}

      {actionType === 'change_priority' && (
        <FormControl fullWidth size="small">
          <InputLabel>Nouvelle priorité *</InputLabel>
          <Select
            value={config.priority || ''}
            label="Nouvelle priorité *"
            onChange={e => onChange({ ...config, priority: e.target.value })}
          >
            {PRIORITY_OPTIONS.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </Select>
        </FormControl>
      )}

      {actionType === 'assign_to' && (
        <FormControl fullWidth size="small">
          <InputLabel>Assigner à *</InputLabel>
          <Select
            value={config.user_id || ''}
            label="Assigner à *"
            onChange={e => onChange({ ...config, user_id: e.target.value })}
          >
            {(members || []).map(m => (
              <MenuItem key={m.id} value={m.id}>{m.full_name || m.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {(actionType === 'notify_user') && (
        <Stack spacing={1}>
          <FormControl fullWidth size="small">
            <InputLabel>Notifier (optionnel)</InputLabel>
            <Select
              value={config.user_id || ''}
              label="Notifier (optionnel)"
              onChange={e => onChange({ ...config, user_id: e.target.value })}
            >
              <MenuItem value=""><em>Assigné à la tâche</em></MenuItem>
              {(members || []).map(m => (
                <MenuItem key={m.id} value={m.id}>{m.full_name || m.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small" fullWidth
            label="Message (optionnel)"
            value={config.message || ''}
            onChange={e => onChange({ ...config, message: e.target.value })}
          />
        </Stack>
      )}

      {actionType === 'notify_manager' && (
        <TextField
          size="small" fullWidth
          label="Message personnalisé (optionnel)"
          value={config.message || ''}
          onChange={e => onChange({ ...config, message: e.target.value })}
        />
      )}
    </Box>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function AutomationBuilder({ projectId, members = [] }) {
  const [automations, setAutomations] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLogs, setShowLogs] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [formName, setFormName] = useState('');
  const [formTrigger, setFormTrigger] = useState('');
  const [formTriggerConfig, setFormTriggerConfig] = useState({});
  const [formAction, setFormAction] = useState('');
  const [formActionConfig, setFormActionConfig] = useState({});
  const [formActive, setFormActive] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await automationService.getByProject(projectId);
      setAutomations(res.data.automations || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [projectId]);

  const loadLogs = useCallback(async () => {
    try {
      const res = await automationService.getLogs(projectId);
      setLogs(res.data.logs || []);
    } catch (e) {
      console.error(e);
    }
  }, [projectId]);

  useEffect(() => {
    load();
    // Déclencher la vérification des tâches en retard
    automationService.overdueCheck(projectId).catch(() => {});
  }, [load, projectId]);

  useEffect(() => {
    if (showLogs) loadLogs();
  }, [showLogs, loadLogs]);

  function openCreate() {
    setEditTarget(null);
    setFormName('');
    setFormTrigger('');
    setFormTriggerConfig({});
    setFormAction('');
    setFormActionConfig({});
    setFormActive(true);
    setError('');
    setDialogOpen(true);
  }

  function openEdit(auto) {
    setEditTarget(auto);
    setFormName(auto.name);
    setFormTrigger(auto.trigger_type);
    setFormTriggerConfig(typeof auto.trigger_config === 'string' ? JSON.parse(auto.trigger_config) : (auto.trigger_config || {}));
    setFormAction(auto.action_type);
    setFormActionConfig(typeof auto.action_config === 'string' ? JSON.parse(auto.action_config) : (auto.action_config || {}));
    setFormActive(auto.is_active === 1 || auto.is_active === true);
    setError('');
    setDialogOpen(true);
  }

  async function handleSave() {
    if (!formName.trim()) { setError('Le nom est requis.'); return; }
    if (!formTrigger) { setError('Choisissez un déclencheur.'); return; }
    if (!formAction) { setError('Choisissez une action.'); return; }

    setSaving(true);
    try {
      const payload = {
        project_id: projectId,
        name: formName.trim(),
        trigger_type: formTrigger,
        trigger_config: formTriggerConfig,
        conditions: [],
        action_type: formAction,
        action_config: formActionConfig,
        is_active: formActive
      };

      if (editTarget) {
        await automationService.update(editTarget.id, payload);
      } else {
        await automationService.create(payload);
      }

      setDialogOpen(false);
      load();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Erreur lors de la sauvegarde');
    }
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!window.confirm('Supprimer cette automatisation ?')) return;
    await automationService.delete(id);
    load();
  }

  async function handleToggle(auto) {
    await automationService.update(auto.id, { is_active: !auto.is_active });
    load();
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BoltOutlined sx={{ color: TRIGGER_COLOR }} />
            Automatisations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Créez des règles qui s'exécutent automatiquement pour automatiser votre workflow.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            size="small" variant="outlined" startIcon={<History />}
            onClick={() => setShowLogs(v => !v)}
          >
            Historique
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            Nouvelle règle
          </Button>
        </Stack>
      </Box>

      {/* Logs */}
      {showLogs && (
        <Accordion defaultExpanded sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight={600}>Historique d'exécution</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {logs.length === 0 ? (
              <Typography sx={{ p: 2 }} color="text.secondary">Aucun historique.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Règle</TableCell>
                    <TableCell>Tâche</TableCell>
                    <TableCell>Action effectuée</TableCell>
                    <TableCell>Statut</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map(log => (
                    <TableRow key={log.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(log.ran_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </TableCell>
                      <TableCell>{log.automation_name}</TableCell>
                      <TableCell>{log.task_title || '—'}</TableCell>
                      <TableCell>{log.action_taken}</TableCell>
                      <TableCell>
                        {log.status === 'success'
                          ? <CheckCircle sx={{ color: ACTION_COLOR, fontSize: 18 }} />
                          : <Tooltip title={log.error_message}><ErrorIcon sx={{ color: 'error.main', fontSize: 18 }} /></Tooltip>
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Liste des automatisations */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : automations.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 5, textAlign: 'center', borderStyle: 'dashed', borderRadius: 3, bgcolor: 'background.default' }}
        >
          <BoltOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary" fontWeight={500}>Aucune automatisation configurée</Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
            Créez votre première règle pour automatiser votre workflow.
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            Créer une règle
          </Button>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {automations.map(auto => (
            <Card
              key={auto.id}
              variant="outlined"
              sx={{
                borderRadius: 2,
                opacity: auto.is_active ? 1 : 0.55,
                transition: 'opacity .2s',
                borderColor: auto.is_active ? TRIGGER_COLOR + '60' : 'divider'
              }}
            >
              <CardContent sx={{ pb: '8px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  {/* Visual flow */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {/* Trigger pill */}
                    <Chip
                      icon={<FlashOn sx={{ fontSize: 14 }} />}
                      label={TRIGGERS.find(t => t.value === auto.trigger_type)?.label || auto.trigger_type}
                      size="small"
                      sx={{ bgcolor: TRIGGER_COLOR + '18', color: TRIGGER_COLOR, fontWeight: 600, border: `1px solid ${TRIGGER_COLOR}40` }}
                    />
                    <ArrowForward sx={{ color: 'text.disabled', fontSize: 16 }} />
                    {/* Action pill */}
                    <Chip
                      icon={<CallSplit sx={{ fontSize: 14 }} />}
                      label={ACTIONS.find(a => a.value === auto.action_type)?.label || auto.action_type}
                      size="small"
                      sx={{ bgcolor: ACTION_COLOR + '18', color: ACTION_COLOR, fontWeight: 600, border: `1px solid ${ACTION_COLOR}40` }}
                    />
                  </Box>

                  {/* Controls */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title={auto.is_active ? 'Désactiver' : 'Activer'}>
                      <Switch
                        size="small"
                        checked={!!auto.is_active}
                        onChange={() => handleToggle(auto)}
                        color="success"
                      />
                    </Tooltip>
                    <Tooltip title="Modifier">
                      <IconButton size="small" onClick={() => openEdit(auto)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton size="small" color="error" onClick={() => handleDelete(auto.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1 }}>
                  {auto.name}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Exécutions : <strong>{auto.run_count || 0}</strong>
                  </Typography>
                  {auto.last_run_at && (
                    <Typography variant="caption" color="text.secondary">
                      Dernière : {new Date(auto.last_run_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Dialog création / édition */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editTarget ? 'Modifier la règle' : 'Nouvelle automatisation'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Nom de la règle *"
              fullWidth size="small"
              value={formName}
              onChange={e => setFormName(e.target.value)}
              placeholder="Ex: Notifier le manager quand tâche terminée"
            />

            {/* ── TRIGGER ── */}
            <Box>
              <Typography
                variant="caption" fontWeight={700} sx={{ color: TRIGGER_COLOR, textTransform: 'uppercase', letterSpacing: 1 }}
              >
                ⚡ Déclencheur
              </Typography>
              <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                <InputLabel>Quand...</InputLabel>
                <Select
                  value={formTrigger}
                  label="Quand..."
                  onChange={e => { setFormTrigger(e.target.value); setFormTriggerConfig({}); }}
                >
                  {TRIGGERS.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TriggerConfig
                triggerType={formTrigger}
                config={formTriggerConfig}
                onChange={setFormTriggerConfig}
              />
            </Box>

            <Divider />

            {/* ── ACTION ── */}
            <Box>
              <Typography
                variant="caption" fontWeight={700} sx={{ color: ACTION_COLOR, textTransform: 'uppercase', letterSpacing: 1 }}
              >
                ✅ Action
              </Typography>
              <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                <InputLabel>Alors...</InputLabel>
                <Select
                  value={formAction}
                  label="Alors..."
                  onChange={e => { setFormAction(e.target.value); setFormActionConfig({}); }}
                >
                  {ACTIONS.map(a => (
                    <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <ActionConfig
                actionType={formAction}
                config={formActionConfig}
                onChange={setFormActionConfig}
                members={members}
              />
            </Box>

            <FormControlLabel
              control={<Switch checked={formActive} onChange={e => setFormActive(e.target.checked)} color="success" />}
              label="Activer immédiatement"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : (editTarget ? 'Mettre à jour' : 'Créer')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
